import React from "react";
import { Box, Typography } from "@mui/material";
import SVGSimpleCheckMark from "@/assets/icons/SVGSimpleCheckMark.tsx";

export interface StepperStep {
  key: number;
  lines: string[];
}

interface StepperProps {
  steps: StepperStep[];
  currentStep: number;
  primaryColor?: string;
  mutedColor?: string;
  activeColor?: string;
  circleSize?: number;
}

const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  primaryColor = "#0076CE",
  mutedColor = "#F2F2F3",
  activeColor = "#E6F1FA",
  circleSize = 40,
}) => {
  return (
    <Box sx={{ position: "relative", px: 0 }}>
      {/* Single row layout with circles and labels stacked */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0,
          position: "relative",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        {steps.map((step) => {
          const isCompleted = step.key < currentStep;
          const isActive = step.key === currentStep;
          return (
            <Box
              key={step.key}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                flex: 1,
              }}
            >
              {/* Circle */}
              <Box
                sx={{
                  width: circleSize,
                  height: circleSize,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isCompleted
                    ? primaryColor
                    : isActive
                    ? activeColor
                    : mutedColor,
                  color: isCompleted
                    ? "#fff"
                    : isActive
                    ? primaryColor
                    : "rgba(0, 0, 0, 0.2)",
                  fontWeight: 600,
                  fontSize: "16px",
                  zIndex: 2,
                }}
              >
                {isCompleted ? (
                  <SVGSimpleCheckMark color="#ffffff" width="16" height="16" />
                ) : (
                  step.key
                )}
              </Box>

              {/* Labels */}
              <Box sx={{ mt: 1, textAlign: "center" }}>
                {step.lines.map((line, lineIndex) => (
                  <Typography
                    key={lineIndex}
                    sx={{
                      fontSize: "12px",
                      color: isActive
                        ? primaryColor
                        : isCompleted
                        ? "#181A1B"
                        : "rgba(0, 0, 0, 0.4)",
                      lineHeight: 1.2,
                      fontWeight: 500,
                    }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Connector lines between circles */}
      {steps.slice(0, -1).map((_, index) => {
        const stepNumber = index + 1;
        const leftPosition = `${16 + index * 20}%`;
        const width = "8%";

        return (
          <Box
            key={`connector-${stepNumber}`}
            sx={{
              position: "absolute",
              left: leftPosition,
              top: `${circleSize / 2}px`,
              width,
              height: "1px",
              backgroundColor:
                currentStep > stepNumber ? primaryColor : "rgba(0, 0, 0, 0.1)",
              zIndex: 1,
            }}
          />
        );
      })}
    </Box>
  );
};

export default Stepper;
